import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useReport } from '../context/ReportContext';
import { useAuth } from '../context/AuthContext';
import ReportForm from '../components/ReportForm';
import TeamStatusCard from '../components/TeamStatusCard';
import MemberReportHistoryPanel from '../components/MemberReportHistoryPanel';
import { summaryAPI } from '../utils/api';

const ReportPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const {
    todayDate,
    currentReport,
    teamStatus,
    loading,
    error,
    fetchTodayDate,
    fetchReport,
    fetchTeamStatus,
    setError
  } = useReport();

  // Initialize data on mount
  useEffect(() => {
    const loadData = async () => {
      const date = await fetchTodayDate();
      if (date) {
        await fetchReport(date);
        await fetchTeamStatus(date);
      }
    };

    loadData();
  }, [fetchTodayDate, fetchReport, fetchTeamStatus]);

  const [sendSummaryLoading, setSendSummaryLoading] = React.useState(false);
  const [sendSummaryResult, setSendSummaryResult] = React.useState(null);
  const [showSendConfirmDialog, setShowSendConfirmDialog] = React.useState(false);
  const [unsubmittedMembers, setUnsubmittedMembers] = React.useState([]);

  const handleReportSubmitSuccess = async (result) => {
    // Refresh team status after successful submission
    if (todayDate) {
      await fetchTeamStatus(todayDate);
    }
  };

  // Handler for leader to send summary
  const handleSendSummary = async () => {
    setSendSummaryLoading(true);
    setSendSummaryResult(null);
    try {
      const res = await summaryAPI.send(todayDate, user.team_id, false);

      if (res.data.requiresConfirmation) {
        setUnsubmittedMembers(res.data.unsubmittedMembers || []);
        setShowSendConfirmDialog(true);
        return;
      }

      setSendSummaryResult({ success: true, message: res.data.message });
    } catch (err) {
      setSendSummaryResult({ success: false, message: err.response?.data?.error || 'Failed to send summary' });
    } finally {
      setSendSummaryLoading(false);
    }
  };

  const handleConfirmSendSummary = async () => {
    setSendSummaryLoading(true);
    setSendSummaryResult(null);
    try {
      const res = await summaryAPI.send(todayDate, user.team_id, true);
      setSendSummaryResult({ success: true, message: res.data.message });
      setShowSendConfirmDialog(false);
      setUnsubmittedMembers([]);
    } catch (err) {
      setSendSummaryResult({ success: false, message: err.response?.data?.error || 'Failed to send summary' });
    } finally {
      setSendSummaryLoading(false);
    }
  };

  const handleRefresh = async () => {
    setError(null);
    if (todayDate) {
      await fetchReport(todayDate);
      await fetchTeamStatus(todayDate);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (loading && !currentReport) {
    return (
      <div className="min-h-screen bg-gray-100 p-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center py-12">
            <div className="text-xl text-gray-600">Loading your report...</div>
          </div>
        </div>
      </div>
    );
  }

  const todayDateFormatted = todayDate
    ? new Date(todayDate).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : 'Today';

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Daily Report</h1>
              <p className="text-gray-600 mt-1">{todayDateFormatted}</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-400"
              >
                {loading ? 'Refreshing...' : '🔄 Refresh'}
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600"
              >
                🚪 Logout
              </button>
            </div>
          </div>

          {/* User Info */}
          <div className="bg-blue-50 border border-blue-200 rounded p-4">
            <p className="text-sm text-gray-600">
              <span className="font-semibold">Logged in as:</span> {user?.full_name}
            </p>
            {user?.team_id ? (
              <p className="text-sm text-gray-600 mt-1">
                <span className="font-semibold">Team:</span> {user?.team_name || 'Loading...'}
              </p>
            ) : (
              <p className="text-sm text-red-600 mt-1">
                <span className="font-semibold">⚠️ Not assigned to a team</span>
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        {error && (
          <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
            Error: {error}
          </div>
        )}


        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Report Form - Takes up 2 columns on large screens */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Submit Your Report</h2>

              {user?.team_id ? (
                <ReportForm onSubmitSuccess={handleReportSubmitSuccess} />
              ) : (
                <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
                  You are not assigned to a team. Please contact your admin to assign you to a team.
                </div>
              )}

              {/* Send Summary Button */}
              {user?.team_id && (
                <div className="mt-8">
                  <button
                    onClick={handleSendSummary}
                    disabled={sendSummaryLoading}
                    className="px-6 py-3 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {sendSummaryLoading ? 'Sending to Teams...' : 'Send Summary to Teams'}
                  </button>
                  {sendSummaryResult && (
                    <div className={`mt-3 p-3 rounded ${sendSummaryResult.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                      {sendSummaryResult.message}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Team Status - Takes up 1 column on large screens */}
          <div className="lg:col-span-1">
            <TeamStatusCard
              teamStatus={teamStatus}
              isLoading={loading}
              error={error}
            />
          </div>
        </div>

        {/* Member Report History */}
        <div className="mt-8">
          {user?.team_id && <MemberReportHistoryPanel />}
        </div>

        {/* Helpful Info */}
        <div className="mt-8 bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">ℹ️ How It Works</h3>
          <ul className="space-y-3 text-gray-700">
            <li className="flex gap-3">
              <span className="text-blue-500 font-bold">1.</span>
              <span>Fill in what you did <strong>Yesterday</strong> and what you plan to do <strong>Today</strong>.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-blue-500 font-bold">2.</span>
              <span>Click <strong>Submit Report</strong> to save your report for today.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-blue-500 font-bold">3.</span>
              <span>Once <strong>all team members</strong> submit, the team summary is automatically locked and sent to Teams.</span>
            </li>
            <li className="flex gap-3">
              <span className="text-blue-500 font-bold">4.</span>
              <span>Check the <strong>Team Submission Status</strong> panel to see who has submitted.</span>
            </li>
          </ul>
        </div>

        {showSendConfirmDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg max-w-md w-full mx-4">
              <div className="p-6 space-y-4">
                <h2 className="text-lg font-bold text-gray-800">Confirm Send Summary</h2>

                <p className="text-gray-700 text-sm">
                  The following members have not submitted a report:
                </p>

                <div className="bg-gray-50 rounded p-3 max-h-48 overflow-y-auto">
                  <ul className="space-y-2">
                    {unsubmittedMembers.map(member => (
                      <li key={member.id} className="flex items-start">
                        <span className="text-gray-400 mr-2">•</span>
                        <div>
                          <p className="text-gray-800 font-medium">{member.name}</p>
                          <p className="text-gray-600 text-xs">{member.email}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-gray-700 font-semibold text-sm">
                  This action will mark them as <span className="text-orange-600">Today: Off</span> and <span className="text-orange-600">Yesterday: N/A</span>.
                </p>

                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowSendConfirmDialog(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmSendSummary}
                    disabled={sendSummaryLoading}
                    className="flex-1 px-4 py-2 bg-blue-500 text-white font-semibold rounded-lg hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                  >
                    {sendSummaryLoading ? 'Sending...' : 'Confirm & Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportPage;
